import type { AdminSettings } from "@/lib/admin-api-types";
import type { ReceiptDocumentData } from "@/lib/document-types";
import { getCategoryLabel } from "@/lib/donation-categories";
import { getDefaultDocumentLogoUrl } from "@/lib/document-asset-url";
import { siteDefaults } from "@/lib/site-config";

export type DonationDocumentSource = {
  id: string;
  donorName: string;
  donorEmail?: string | null;
  phone?: string | null;
  amount: number;
  category: string;
  paidAt: string | null;
  pan?: string | null;
  address?: string | null;
  receiptNumber?: string | null;
  razorpayPaymentId?: string | null;
};

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

function twoDigits(n: number): string {
  if (n < 20) return ONES[n] ?? "";
  const t = Math.floor(n / 10);
  const o = n % 10;
  return `${TENS[t] ?? ""}${o ? ` ${ONES[o]}` : ""}`.trim();
}

function threeDigits(n: number): string {
  if (n === 0) return "";
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const head = h ? `${ONES[h]} Hundred` : "";
  const tail = rest ? twoDigits(rest) : "";
  return [head, tail].filter(Boolean).join(" ");
}

/** Convert rupee amount to words (Indian numbering, whole rupees). */
export function amountInWords(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return "";
  const n = Math.floor(amount);
  if (n === 0) return "Zero Rupees Only";

  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const hundred = n % 1000;

  const parts: string[] = [];
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${threeDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigits(thousand)} Thousand`);
  if (hundred) parts.push(threeDigits(hundred));

  return `${parts.join(" ")} Rupees Only`;
}

export function formatDocumentAmount(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDocumentDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

function formatDocumentDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));
}

function buildQrCodeSrc(donationId: string): string {
  const verifyUrl = `${siteDefaults.url.replace(/\/$/, "")}/donate?ref=${donationId}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=96x96&data=${encodeURIComponent(verifyUrl)}`;
}

function currentFinancialYear(): string {
  const now = new Date();
  const year = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? year : year - 1;
  const endYear = startYear + 1;
  return `${startYear}-${String(endYear).slice(-2)}`;
}

export function buildReceiptDocumentData(
  donation: DonationDocumentSource,
  settings: AdminSettings
): ReceiptDocumentData {
  const paidAt = donation.paidAt ?? new Date().toISOString();
  const financialYear = currentFinancialYear();
  const receiptNumber =
    donation.receiptNumber ??
    `${settings.receiptPrefix}/${financialYear.replace("-", "/")}/0000`;

  return {
    foundationName: settings.foundationName,
    foundationLegalStatus: settings.foundationLegalStatus,
    foundationAddress: settings.foundationAddress,
    foundationPan: settings.foundationPan,
    foundationEmail: settings.foundationEmail,
    foundationPhone: settings.foundationPhone,
    logoUrl: settings.logoUrl.trim() || getDefaultDocumentLogoUrl(),
    registration80G: settings.registration80G,
    registration80GValidFrom: settings.registration80GValidFrom || "—",
    registration80GValidTo: settings.registration80GValidTo || "—",
    receiptNumber,
    donationDate: formatDocumentDate(paidAt),
    donationDateTime: formatDocumentDateTime(paidAt),
    donorName: donation.donorName,
    donorEmail: donation.donorEmail ?? "—",
    donorAddress: donation.address?.trim() ? donation.address.trim() : "—",
    donorPan: donation.pan?.trim() ? donation.pan : "Not provided",
    donorPhone: donation.phone?.trim() ? donation.phone.trim() : "—",
    amount: donation.amount,
    amountFormatted: formatDocumentAmount(donation.amount),
    amountInWords: amountInWords(donation.amount),
    category: getCategoryLabel(donation.category),
    paymentMode: "Online (Razorpay)",
    razorpayPaymentId: donation.razorpayPaymentId ?? "—",
    referenceId: donation.id,
    financialYear,
    certificateId: donation.id,
    issuedAt: formatDocumentDate(new Date().toISOString()),
    signatoryName: settings.signatoryName,
    signatoryTitle: settings.signatoryTitle,
    websiteUrl: siteDefaults.displayHost,
    tagline: "Empowering Lives. Enabling Change.",
    qrCodeDataUri: buildQrCodeSrc(donation.id),
  };
}
